import { useEffect, useRef, useState } from 'react'

/**
 * Time24Input — plain-text HH:MM entry, always 24-hour, no AM/PM.
 *
 * Native <input type="time"> stores its value as 24-hour "HH:mm" internally,
 * but the *displayed* picker UI follows the browser/OS locale — on Chrome and
 * Safari with an en-US locale that's always a 12-hour AM/PM picker, and
 * there's no HTML attribute that can force a 24-hour display (confirmed via
 * MDN + long-standing browser bug reports; `lang`/`dir` overrides don't
 * affect it in current Chrome/Safari). For a field-ops app operating on
 * ICS 24-hour clock convention, silently showing 12hr AM/PM would risk a
 * genuine transcription error (2:00 PM logged as 2:00, mistaken for 0200).
 *
 * This component sidesteps the native picker entirely: a plain text input
 * with input masking (auto-inserts the colon, restricts to 4 digits,
 * clamps to valid HH:MM on blur) so the displayed and typed format is
 * always unambiguously 24-hour, on every browser/OS/locale.
 *
 * Value/onChange contract matches <input type="time">: "HH:mm" 24-hour
 * string in, "HH:mm" 24-hour string out — drop-in replacement.
 */
export function Time24Input({
  value,
  onChange,
  onBlur,
  className,
  autoFocus,
}: {
  value: string
  onChange: (val: string) => void
  onBlur?: () => void
  className?: string
  autoFocus?: boolean
}) {
  const [draft, setDraft] = useState(value || '')
  const inputRef = useRef<HTMLInputElement>(null)

  // Keep local draft in sync when the value changes externally (e.g. parent
  // resets the form), but don't fight the user's own keystrokes mid-edit.
  useEffect(() => { setDraft(value || '') }, [value])

  const digitsOnly = (s: string) => s.replace(/\D/g, '').slice(0, 4)

  const formatDraft = (digits: string) => {
    if (digits.length <= 2) return digits
    return `${digits.slice(0, 2)}:${digits.slice(2)}`
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = digitsOnly(e.target.value)
    const formatted = formatDraft(digits)
    setDraft(formatted)
    if (digits.length === 4) {
      const h = Math.min(23, parseInt(digits.slice(0, 2), 10) || 0)
      const m = Math.min(59, parseInt(digits.slice(2), 10) || 0)
      const clamped = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      setDraft(clamped)
      onChange(clamped)
    }
  }

  const commit = () => {
    const digits = digitsOnly(draft)
    if (digits.length === 0) {
      setDraft('')
      onChange('')
      onBlur?.()
      return
    }
    // Pad a partial entry (e.g. "9" -> 09:00, "930" -> 09:30) rather than
    // silently discarding it.
    const padded = digits.padEnd(4, '0')
    const h = Math.min(23, parseInt(padded.slice(0, 2), 10) || 0)
    const m = Math.min(59, parseInt(padded.slice(2), 10) || 0)
    const clamped = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    setDraft(clamped)
    onChange(clamped)
    onBlur?.()
  }

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      autoFocus={autoFocus}
      placeholder="HH:MM"
      maxLength={5}
      value={draft}
      onChange={handleChange}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') { commit(); inputRef.current?.blur() } }}
      className={className ?? 'w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500'}
    />
  )
}
